/**
 * Admin API client with transparent re-auth on 401.
 *
 * Access tokens expire after ~15 min by design; the refresh token lives
 * for 30 days. On 401 we silently call `/api/auth/refresh` (which uses
 * the refresh cookie) and retry the original request exactly once.
 * Concurrent 401s share a single in-flight refresh.
 */
const BASE = import.meta.env.VITE_API_BASE ?? 'http://localhost:4000';

export class ApiError extends Error {
  constructor(public status: number, public reference: string, message: string, public raw?: unknown) {
    super(message);
  }
}

interface Init extends Omit<RequestInit, 'body' | 'headers'> {
  body?: unknown;
  headers?: Record<string, string>;
  query?: Record<string, string | number | undefined>;
}

let refreshing: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    try {
      const res = await fetch(`${BASE}/api/auth/refresh`, { method: 'POST', credentials: 'include' });
      return res.ok;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

export async function api<T>(path: string, init: Init = {}, isRetry = false): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json', ...(init.headers ?? {}) };
  let url = `${BASE}${path.startsWith('/') ? '' : '/'}${path}`;
  if (init.query) {
    const params = new URLSearchParams();
    Object.entries(init.query).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
    });
    const qs = params.toString();
    if (qs) url += (url.includes('?') ? '&' : '?') + qs;
  }
  if (init.body !== undefined && !(init.body instanceof FormData)) headers['content-type'] = 'application/json';
  const res = await fetch(url, {
    method: init.method ?? (init.body !== undefined ? 'POST' : 'GET'),
    credentials: 'include',
    headers,
    body: init.body === undefined ? undefined : init.body instanceof FormData ? init.body : JSON.stringify(init.body),
  });

  if (
    res.status === 401 &&
    !isRetry &&
    !path.includes('/api/auth/refresh') &&
    !path.includes('/api/auth/login') &&
    !path.includes('/api/auth/register')
  ) {
    const ok = await tryRefresh();
    if (ok) return api<T>(path, init, true);
    // Refresh failed → session is dead. Notify the AuthProvider so it
    // clears `me` and the route guards send the user to /login.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('tc:session-expired'));
    }
  }

  const text = await res.text();
  let json: any = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    /* */
  }
  if (!res.ok) throw new ApiError(res.status, json?.reference ?? 'HTTP', json?.message ?? res.statusText, json);
  return json as T;
}
